import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Carecommendation, Period } from 'lumen/models';
import type {
  DiscountOverride,
  ExactPriceOverride,
  PriceRangeOverride,
} from 'lumen/models/override';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  type MealTypeValues,
  RATE_NO_VALUE,
} from 'lumen/utils/constants/general-constants';
import type {
  OpenRoomPricingFixedCategory,
  OverrideVariant,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

export interface ComparisonRate {
  best_flex: boolean;
  city_tax_incl: boolean;
  currency: string;
  difference_absolute: number;
  difference_relative: number;
  hotelinfo: number;
  max_persons: number;
  mealtype_included: number;
  message: string | null;
  ota: string;
  other_taxes_incl: boolean;
  period: string;
  preferred_rate: boolean;
  room_name: string;
  room_type: string;
  value: number;
  vat_incl: boolean;
}

type CarateOverride =
  | ExactPriceOverride
  | DiscountOverride
  | PriceRangeOverride;

export default class Carate extends Model {
  [Type] = 'carate' as const;

  @belongsTo('carecommendation', { async: false, inverse: 'carate' })
  declare carecommendation: Carecommendation | null;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;
  @belongsTo('override', { async: false, inverse: 'carates' })
  declare override: CarateOverride | null;

  // Belongs to a period model object.
  @belongsTo('period', { async: false, inverse: 'carates' })
  declare period: Period;

  // Boolean indicating if autopilot is enabled for this carate (will be false if Autopilot isn't supported by the hotel)
  @attr('boolean') declare autopilotEnabled: boolean;
  // Priority for showing carates by average room price (lower is better)
  // Note: base room will have a priority of -1
  @attr('number') declare averagePricePriority: number;
  @attr('boolean') declare baseRoom: boolean;
  @attr('boolean') declare bestFlex: boolean;
  @attr('string') declare cemaCategory: OpenRoomPricingFixedCategory;
  @attr() declare comparisonRates: ComparisonRate[];
  @attr('string') declare currency: SupportedCurrencies;
  /* BAR discount applied to the current PMS price on dynamic rate views (decimal, e.g. 0.05 = 5%). Null on BAR and non-BAR-derived rates. */
  @attr('number') declare currentDiscount: number | null;
  @attr('hoteldate') declare extractDate: NonNullable<HoteldateTransformReturn>;
  /** Datetime when the data has been acquired. Where the data has been acquired from can differ depending on the endpoint (e.g. PMS or channel) */
  @attr('dayjs') declare extractDatetime: DayjsTransformReturn;
  @attr('number') declare los: number;
  @attr('number') declare maxPersons: number;
  @attr('number') declare mealtypeIncluded: MealTypeValues;
  @attr('string') declare message: string;
  @attr('string') declare ota: string;
  @attr('string') declare pmsRoomtype: string;
  @attr('number') declare pmsValue: number;
  @attr('boolean') declare preferredRate: boolean;
  @attr('string') declare rateCode: string;
  @attr('string') declare roomName: string;
  @attr('string') declare roomType: string;
  /** Actual shopped channel value. Will not be available on all Carates. Currently being tested in Smart Distribution */
  @attr('number') declare shoppedValue: number | undefined;
  @attr('number') declare userSetMaximum: number | null;
  @attr('number') declare userSetMinimum: number | null;
  /** PMS + offset value */
  @attr('number') declare value: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get dynamicRatePricingPriceToShow(): number {
    return this.carecommendation?.priceToShow ?? this.value;
  }

  /* `null` means no discount applies (BAR or a non-derived rate) → render nothing.
     `0` is a real 0% discount (a dynamic rate priced equal to its parent) → shown. */
  get formattedCurrentDiscount(): string | null {
    if (this.currentDiscount == null) {
      return null;
    }
    return this.intl.formatNumber(this.currentDiscount, {
      style: 'percent',
      maximumFractionDigits: 0,
    });
  }

  /**
   * Shows the recommendation's price when one exists (which already reflects any
   * override - the BE bakes overrides into recommendations); otherwise falls back
   * to the carate's own rate.
   */
  get formattedDynamicRatePricingPriceToShow(): string | null {
    if (this.carecommendation) {
      return this.carecommendation.formattedPriceToShow;
    }

    return this.formattedRate;
  }

  get formattedMessage(): string {
    if (this.message) {
      return this.intl.t(this.message);
    }

    return '';
  }

  get formattedPmsRate(): string | null {
    return this.hotelAndMarketService.formatRate(this.pmsValue, true, false);
  }

  get formattedPriceChangeSinceRefDaysAgo(): string | null {
    return this.hotelAndMarketService.formatRate(
      this.priceChangeSinceRefDaysAgo,
      true,
      false, // no long format
      undefined,
      true, // include sign
    );
  }

  get formattedRate(): string {
    /* Note: used formatRate instead of formatRateObject here for now.
      think formatRateObject can also work, but now formatRate is used everywhere in this model + there are some slight differences with regular rate model
    */
    const formattedRate = this.hotelAndMarketService.formatRate(
      this,
      true,
      false,
    );

    if (!formattedRate) {
      return this.intl.t('general.missing');
    }
    return formattedRate;
  }

  get formattedShoppedValue(): string {
    if (this.shoppedValue === undefined) {
      return this.intl.t('general.missing');
    }

    return (
      this.hotelAndMarketService.formatRate(this.shoppedValue, true, false) ||
      this.intl.t('general.missing')
    );
  }

  get hasOverride(): boolean {
    return this.override !== null;
  }

  get hasPmsValue(): boolean {
    return this.pmsValue !== RATE_NO_VALUE;
  }

  /*
    Note: a CA rate can also have a rate value + a message at the same time:
    -> when there's a rate in the PMS, but not on Booking.com
   */
  get hasValue(): boolean {
    return this.value !== RATE_NO_VALUE;
  }

  get hasValueAndMessage(): boolean {
    return this.hasValue && Boolean(this.message);
  }

  /**
   * Returns a boolean if the shopped rate is lower than the CM value (PMS + offset) - (Smart Distribution)
   * If the shopped rate is lower than the CM value, a discount is detected
   */
  get isRateDiscountDetected(): boolean {
    if (!this.shoppedValue) {
      return false;
    }

    // Came up in testing session - strip decimals - avoid rounding issues (e.g. carate.value of 178.18 vs. shoppedValue of 178)
    // Added this for all shopped vs. value comparisons for now. Evaluate later if it makes sense to add specific getters for this.
    return Math.trunc(this.shoppedValue) < Math.trunc(this.value);
  }

  /**
   * Returns a boolean if the shopped rate is higher than the CM value (PMS + offset) - (Smart Distribution)
   */
  get isRateHigherThanExpected(): boolean {
    if (!this.shoppedValue) {
      return false;
    }

    return Math.trunc(this.shoppedValue) > Math.trunc(this.value);
  }

  /**
   * Returns a boolean whether the shopped rate is exactly the same as the CM value (PMS + offset) - (Smart Distribution)
   */
  get isRateShoppedValueAsExpected(): boolean {
    if (!this.shoppedValue) {
      return false;
    }

    return Math.trunc(this.shoppedValue) === Math.trunc(this.value);
  }

  get isSoldOut(): boolean {
    return this.message === 'rates.soldout';
  }

  get overrideVariant(): OverrideVariant | undefined {
    return getOverrideVariant(this.override?.type);
  }

  // Currently only reference period of 1 day (yesterday) is supported i.e. price change since yesterday
  get priceChangeSinceRefDaysAgo(): number | null {
    if (
      this.hasValue &&
      this.comparisonRates?.[0] &&
      Math.abs(this.comparisonRates[0].difference_absolute) >= 1 // avoid showing +/-0 in the UI
    ) {
      return this.comparisonRates[0].difference_absolute;
    }

    return null;
  }

  get showNonPreferredRateWarning(): boolean {
    return !this.preferredRate && !this.message;
  }

  get showOverride(): boolean {
    return this.hasOverride && !this.carecommendation?.hasException;
  }

  get type(): 'carate' {
    return this[Type];
  }

  // To log the carate object in the console, we need to use the toJSON method, otherwise we get the proxy object
  toJSON(): unknown {
    return this.serialize();
  }
}
